// Módulo Notas (Bloque 1) -- utilidades del front. El backend es la autoridad de permisos; aquí solo se arma la pantalla.
import api, { formatApiError } from "./api";
import { d } from "./dialer";

export const FOUNDER_EMAIL = "consulting@vzwhelpscenter.com";
export const ROLES_NOTAS = ["admin", "gerente", "asistente", "bilingue"];

export const nt = {
  get: (path, config) => api.get(`/notes-center${path}`, config),
  post: (path, body, config) => api.post(`/notes-center${path}`, body ?? {}, config),
  put: (path, body, config) => api.put(`/notes-center${path}`, body ?? {}, config),
  delete: (path, config) => api.delete(`/notes-center${path}`, config),
};

export const isFounderUser = (user) => (user?.email || "").toLowerCase() === FOUNDER_EMAIL;
export const puedeNotas = (user) => !!user && ROLES_NOTAS.includes(user.role);
/** «Equipo» solo para admin/gerente; el Founder NUNCA lo ve (el servidor además le responde 403). */
export const puedeEquipo = (user) => !!user && ["admin", "gerente"].includes(user.role) && !isFounderUser(user);
export const esSupervisor = (user) => !!user && ["admin", "gerente"].includes(user.role);
export const myId = (user) => String(user?._id || user?.id || "");

export const COLORES = ["#FFD84D", "#FF8A2A", "#3DDC97", "#6CC8F0", "#c9a0ff", "#ff8fa3"];
export const colorDe = (nota) => nota?.color || COLORES[0];

// ---- Color de la hoja: "" = oscuro (por defecto); cualquier #hex tiñe TODA la hoja y el texto se ajusta por luminancia ----
export const COLOR_BLANCO = "#FFFFFF";
export const COLOR_PROPIO_BASE = "#2b6cb0";
/** Normaliza #rgb / #rrggbb a "#rrggbb" en minúsculas; cualquier otra cosa (incluido "") devuelve null = hoja oscura. */
export function hexValido(c) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(c || "").trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split("").map((x) => x + x).join("") : m[1];
  return `#${h.toLowerCase()}`;
}
export function luminancia(hex) {
  const h = hexValido(hex);
  if (!h) return 0;
  const n = parseInt(h.slice(1), 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}
/** Paleta de la hoja para un color de nota: {bg, tx, num, claro} o null (hoja oscura de siempre). Mismo criterio que la maqueta (claro si luminancia > 0.6). */
export function paletaNota(color) {
  const bg = hexValido(color);
  if (!bg) return null;
  const claro = luminancia(bg) > 0.6;
  return { bg, claro, tx: claro ? "#17171b" : "#ffffff", num: claro ? "#b45309" : "#ffd9b8" };
}

// ---- Números de teléfono: mismo criterio que el normalizador del servidor (EE. UU., +1) ----
export function normalizePhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}
export function fmtTel(p) {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(p || "");
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : p || "";
}
const PHONE_RE = /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}/g;

/** Parte un texto en trozos [{t:"text",v}|{t:"num",v,phone}] para marcar los números clicables. */
export function splitNumbers(text) {
  const src = String(text || "");
  const out = [];
  let last = 0;
  PHONE_RE.lastIndex = 0;
  let m;
  while ((m = PHONE_RE.exec(src)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const before = src[start - 1];
    const after = src[end];
    const phone = normalizePhone(m[0]);
    if (!phone || (before && /\d/.test(before)) || (after && /\d/.test(after))) {
      PHONE_RE.lastIndex = start + 1;   // pegado a otros dígitos: no es un teléfono; seguir buscando
      continue;
    }
    if (start > last) out.push({ t: "text", v: src.slice(last, start) });
    out.push({ t: "num", v: m[0], phone });
    last = end;
  }
  if (last < src.length) out.push({ t: "text", v: src.slice(last) });
  return out;
}

/** Contador de un número dentro de una nota (el servidor lo calcula; aquí solo se busca). */
export function contadorDe(counts, phone) {
  return (counts || []).find((c) => c.phone === phone) || null;
}

/** Busca el teléfono reconocido que contiene la posición del cursor (para abrir «¿por dónde llamar?» sin salir del campo); null si no hay ninguno ahí. */
export function numeroEnPosicion(text, pos) {
  const parts = splitNumbers(text);
  let acc = 0;
  for (const p of parts) {
    const fin = acc + p.v.length;
    if (p.t === "num" && pos >= acc && pos <= fin) return p.phone;
    acc = fin;
  }
  return null;
}

/** Contadores en vivo por número (`/attempts/counters`): una sola llamada agrupada; si hay más de 100 teléfonos se parte en lotes. */
export const CONTADORES_LOTE = 100;
export async function fetchCounters(phones) {
  const unicos = Array.from(new Set((phones || []).filter(Boolean)));
  if (!unicos.length) return [];
  const lotes = [];
  for (let i = 0; i < unicos.length; i += CONTADORES_LOTE) lotes.push(unicos.slice(i, i + CONTADORES_LOTE));
  const partes = await Promise.all(lotes.map((lote) => d.get("/attempts/counters", { params: { phones: lote.join(",") } })));
  return partes.flatMap((r) => r.data?.items || []);
}

export function errorNota(e) {
  const detail = e?.response?.data?.detail;
  const st = e?.response?.status;
  if (st === 404) return "Esa nota ya no existe o no es tuya.";
  if (st === 403) return "No tienes permiso para esta acción.";
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (detail.code === "version_conflict") return "Esta nota cambió en otra ventana.";
    if (detail.code === "account_unavailable") return "Esa cuenta ya no está disponible. Elige otra.";
    if (detail.msg) return detail.msg;
  }
  return formatApiError(detail ?? e?.message);
}
export const esConflicto = (e) => e?.response?.status === 409 && e?.response?.data?.detail?.code === "version_conflict";

export function nuevoId(prefix = "a") {
  const rnd = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID().replace(/-/g, "") : `${Date.now()}${Math.random().toString(36).slice(2)}`;
  return `${prefix}${rnd}`.slice(0, 32); // el servidor acepta como máximo 32 en ids de filas/tareas
}

// ---- Fechas legibles en español (hora de Santo Domingo) ----
export function parseIso(iso) {
  if (!iso) return null;
  const s = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
export function fmtFechaCorta(iso, now = new Date()) {
  const d = parseIso(iso);
  if (!d) return "";
  const tz = "America/Santo_Domingo";
  const dia = (x) => x.toLocaleDateString("es-DO", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
  if (dia(d) === dia(now)) return `HOY ${d.toLocaleTimeString("es-DO", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true })}`.toUpperCase();
  const ayer = new Date(now.getTime() - 86400000);
  if (dia(d) === dia(ayer)) return "AYER";
  return d.toLocaleDateString("es-DO", { timeZone: tz, day: "numeric", month: "short" }).replace(".", "").toUpperCase();
}

// ---- Entrega «nota -> Centro de Llamadas» (se guarda un momento; la caducidad del intento la manda el servidor) ----
export const PRECARGA_KEY = "nt.precarga";
export const PRECARGA_TTL_MS = 15 * 60 * 1000;
// La entrega tiene DUEÑO (el id de quien la guardó): otra persona en el mismo navegador nunca la recibe. NotasProvider (envuelve toda la app)
// avisa quién es el usuario actual con fijarUsuarioPrecarga; al cambiar de usuario o cerrar sesión lo ajeno se borra. Sin dueño conocido = no hay entrega.
let precargaDueno = "";
function leerPrecarga() {
  try { const raw = localStorage.getItem(PRECARGA_KEY); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
}
export function limpiarPrecarga() {
  try { localStorage.removeItem(PRECARGA_KEY); } catch (e) { /* sin almacenamiento */ }
}
export function fijarUsuarioPrecarga(uid) {
  precargaDueno = String(uid || "");
  const v = leerPrecarga();
  if (v && (!precargaDueno || v.owner !== precargaDueno)) limpiarPrecarga();
}
export function guardarPrecarga(data) {
  if (!precargaDueno) return;   // sin saber quién es el usuario no se guarda nada que otro pueda recibir
  try { localStorage.setItem(PRECARGA_KEY, JSON.stringify({ ...data, owner: precargaDueno, at: Date.now() })); } catch (e) { /* sin almacenamiento: el teléfono igual busca el intento pendiente */ }
}
export function tomarPrecarga(now = Date.now()) {
  try {
    const raw = localStorage.getItem(PRECARGA_KEY);
    if (!raw) return null;
    localStorage.removeItem(PRECARGA_KEY);
    const v = JSON.parse(raw);
    if (!v || !v.phone || !precargaDueno || v.owner !== precargaDueno || now - Number(v.at) > PRECARGA_TTL_MS) return null;
    return v;
  } catch (e) { return null; }
}

/** Mira la entrega SIN consumirla (misma validación que tomarPrecarga: dueño, teléfono y caducidad). Una ajena o vencida solo se ignora. */
export function verPrecarga(now = Date.now()) {
  const v = leerPrecarga();
  if (!v || !v.phone || !precargaDueno || v.owner !== precargaDueno || now - Number(v.at) > PRECARGA_TTL_MS) return null;
  return v;
}
/** Devuelve una entrega ya tomada (si quien la tomó se desmontó antes de aplicarla). No pisa una entrega más nueva. */
export function devolverPrecarga(v) {
  if (!v || !precargaDueno || v.owner !== precargaDueno) return;
  try { if (!localStorage.getItem(PRECARGA_KEY)) localStorage.setItem(PRECARGA_KEY, JSON.stringify(v)); } catch (e) { /* sin almacenamiento */ }
}

// ---- Resultado de llamada en un toque (POST /dialer/attempts/{id}/result) ----
export const RESULTADOS_RAPIDOS = [
  { key: "sin_respuesta", label: "No contestó", corto: "no contestó" },
  { key: "buzon_confirmado", label: "Buzón", corto: "buzón" },
  { key: "contestada", label: "Contestó", corto: "contestó" },
  { key: "llamada_mala", label: "Número malo", corto: "número malo" },
];
export const corteResultado = (result) => RESULTADOS_RAPIDOS.find((r) => r.key === result)?.corto || result || "";

export async function postResultadoRapido(attemptId, result) {
  const r = await d.post(`/attempts/${attemptId}/result`, { result });
  return r.data;
}
/** Mensaje claro en español para los 409/422 propios de este toque. */
export function mensajeResultadoError(e) {
  const st = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (st === 404) return "Ese intento ya no existe o no es tuyo.";
  if (st === 422) return "Ese resultado no es válido.";
  if (st === 409 && detail && typeof detail === "object") {
    if (detail.code === "attempt_result_conflict") return `Ya se había registrado otro resultado: ${corteResultado(detail.result)}.`;
    if (detail.code === "attempt_not_confirmable") return "Ese intento ya venció o se cerró de otra forma. Usa el registro retroactivo si de verdad llamaste.";
    if (detail.code === "call_still_live") return "La llamada todavía está en curso.";
  }
  return formatApiError(detail ?? e?.message);
}
/** «×N · último: buzón hace 2 d» a partir de `last_result:{result,at}` que trae /attempts/counters. */
export function etiquetaUltimoResultado(lastResult) {
  if (!lastResult?.result) return "";
  const corto = corteResultado(lastResult.result);
  const hace = fmtHaceCorto(lastResult.at);
  return hace ? `último: ${corto} ${hace}` : `último: ${corto}`;
}
function fmtHaceCorto(iso) {
  const d0 = parseIso(iso);
  if (!d0) return "";
  const segs = Math.max(0, Math.round((Date.now() - d0.getTime()) / 1000));
  if (segs < 60) return "hace un momento";
  const min = Math.round(segs / 60);
  if (min < 60) return `hace ${min} min`;
  const horas = Math.round(min / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.round(horas / 24);
  return `hace ${dias} d`;
}

// ---- Último intento registrado por número (para el toque de resultado sin tener que volver a abrir «¿por dónde llamar?») ----
// Solo en memoria/sesión de este navegador; no es dato del servidor. Se limpia igual que la precarga cuando cambia de usuario.
const ULTIMO_INTENTO_KEY = "nt.ultimoIntento";
let ultimoIntentoDueno = "";
function leerUltimosIntentos() {
  try { return JSON.parse(sessionStorage.getItem(ULTIMO_INTENTO_KEY) || "{}") || {}; } catch (e) { return {}; }
}
export function fijarUsuarioUltimoIntento(uid) {
  ultimoIntentoDueno = String(uid || "");
  try { sessionStorage.removeItem(ULTIMO_INTENTO_KEY); } catch (e) { /* sin almacenamiento */ }
}
export function recordarIntento(phone, attemptId) {
  if (!phone || !attemptId || !ultimoIntentoDueno) return;
  try {
    const mapa = leerUltimosIntentos();
    mapa[phone] = { id: attemptId, owner: ultimoIntentoDueno, at: Date.now() };
    sessionStorage.setItem(ULTIMO_INTENTO_KEY, JSON.stringify(mapa));
  } catch (e) { /* sin almacenamiento: el toque simplemente no aparecerá */ }
}
export const INTENTO_TTL_MS = 20 * 60 * 1000;   // igual al margen de los 15 min de «confirmable» + holgura
export function intentoDe(phone) {
  if (!phone || !ultimoIntentoDueno) return null;
  const v = leerUltimosIntentos()[phone];
  if (!v || v.owner !== ultimoIntentoDueno || Date.now() - v.at > INTENTO_TTL_MS) return null;
  return v.id;
}
export function olvidarIntento(phone) {
  if (!phone) return;
  try {
    const mapa = leerUltimosIntentos();
    delete mapa[phone];
    sessionStorage.setItem(ULTIMO_INTENTO_KEY, JSON.stringify(mapa));
  } catch (e) { /* sin almacenamiento */ }
}

// ---- Recordatorios «Volver a llamar» (prefijo /notes-v2) ----
export const nv = {
  get: (path, config) => api.get(`/notes-v2${path}`, config),
  post: (path, body, config) => api.post(`/notes-v2${path}`, body ?? {}, config),
};
export async function crearRecordatorio({ phone, noteId, remindAt, text, reminderId }) {
  const r = await nv.post("/reminders", { reminder_id: reminderId || nuevoId("r"), phone, note_id: noteId || undefined, remind_at: remindAt, text: text || undefined });
  return r.data;
}
export async function listarRecordatorios(params = {}) {
  const r = await nv.get("/reminders", { params: { state: "abiertos", ...params } });
  return r.data;
}
export const accionRecordatorio = (id, accion, body) => nv.post(`/reminders/${id}/${accion}`, body);
export function mensajeRecordatorioError(e) {
  const st = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (st === 404) return "Ese recordatorio ya no existe o no es tuyo.";
  if (st === 422) return "Revisa el número, el texto o la hora (no puede ser en el pasado ni muy lejana).";
  if (st === 409 && detail?.code === "reminders_limit") return "Ya tienes 200 recordatorios abiertos. Cierra alguno antes de crear otro.";
  if (st === 409 && detail?.code === "reminder_id_in_use") return "Ese recordatorio ya existía con otros datos.";
  if (st === 409 && detail?.code === "reminder_not_open") return "Ese recordatorio ya estaba cerrado.";
  return formatApiError(detail ?? e?.message);
}
/** Atajos de hora para «Volver a llamar»: todas devuelven un ISO en el futuro. */
export function atajosHora(now = new Date()) {
  const en = (min) => new Date(now.getTime() + min * 60000).toISOString();
  const aLasHoy = (h) => { const dt = new Date(now); dt.setHours(h, 0, 0, 0); if (dt <= now) dt.setDate(dt.getDate() + 1); return dt.toISOString(); };
  const aLasManana = (h) => { const dt = new Date(now); dt.setDate(dt.getDate() + 1); dt.setHours(h, 0, 0, 0); return dt.toISOString(); };
  return [
    { key: "1h", label: "En 1 hora", iso: en(60) },
    { key: "hoy5", label: "Hoy 5 pm", iso: aLasHoy(17) },
    { key: "man9", label: "Mañana 9 am", iso: aLasManana(9) },
  ];
}

// ---- Nombre del cliente al lado del número (POST /notes-v2/client-names, en lote) ----
export const NOMBRES_LOTE = 100;
let pendNombresPhones = new Set();
let pendNombresResolvers = [];
let pendNombresScheduled = false;
function scheduleNombre(phone) {
  return new Promise((resolve) => {
    pendNombresPhones.add(phone);
    pendNombresResolvers.push({ phone, resolve });
    if (pendNombresScheduled) return;
    pendNombresScheduled = true;
    Promise.resolve().then(async () => {
      const phones = Array.from(pendNombresPhones).slice(0, NOMBRES_LOTE);
      const resolvers = pendNombresResolvers;
      pendNombresPhones = new Set(); pendNombresResolvers = []; pendNombresScheduled = false;
      let items = [];
      try { const r = await nv.post("/client-names", { phones }); items = r.data?.items || []; } catch (e) { items = []; }
      const byPhone = new Map(items.map((it) => [it.phone, it]));
      resolvers.forEach(({ phone: p, resolve: res }) => res(byPhone.get(p) || null));
    });
  });
}
const NOMBRE_CACHE_TTL_MS = 20000;   // caché breve: mostrar/ocultar el nombre con el hover no debe repetir la petición cada vez
let nombreCache = new Map();   // phone -> { at, item }
/** Solo para pruebas: resetea el micro-lote y la caché entre casos. */
export function _resetNombresClienteLote() { pendNombresPhones = new Set(); pendNombresResolvers = []; pendNombresScheduled = false; nombreCache = new Map(); }
export function fetchClientName(phone) {
  if (!phone) return Promise.resolve(null);
  const c = nombreCache.get(phone);
  if (c && Date.now() - c.at < NOMBRE_CACHE_TTL_MS) return Promise.resolve(c.item);
  return scheduleNombre(phone).then((item) => { nombreCache.set(phone, { at: Date.now(), item }); return item; });
}

// ---- Limpiar lista pegada: repetidos y números mal escritos, sin borrar nada sin que el usuario lo acepte ----
/** Analiza un texto que se acaba de pegar: cuenta cuántos números reconocidos son repetidos y cuántos trozos "parecen" un número pero no lo son
 * (demasiados dígitos juntos, o un bloque de 7-15 dígitos que `normalizePhone` no acepta). Devuelve null si no hay nada que limpiar. */
export function analizarListaPegada(text) {
  const src = String(text || "");
  if (!src.trim()) return null;
  const vistos = new Set();
  let repetidos = 0;
  const parts = splitNumbers(src);
  for (const p of parts) if (p.t === "num") { if (vistos.has(p.phone)) repetidos += 1; else vistos.add(p.phone); }
  const MALO_RE = /\b\d{7,15}\b/g;
  let malos = 0;
  let m;
  MALO_RE.lastIndex = 0;
  while ((m = MALO_RE.exec(src)) !== null) { if (!normalizePhone(m[0])) malos += 1; }
  if (!repetidos && !malos) return null;
  return { repetidos, malos };
}
/** Quita las líneas con números repetidos (deja la primera aparición) y las líneas cuyo único contenido es un número mal escrito. */
export function limpiarListaPegada(text) {
  const vistos = new Set();
  const lineas = String(text || "").split("\n").filter((linea) => {
    const parts = splitNumbers(linea).filter((p) => p.t === "num");
    if (parts.length === 1 && linea.trim() === parts[0].v) {
      const phone = parts[0].phone;
      if (vistos.has(phone)) return false;
      vistos.add(phone);
      return true;
    }
    for (const p of parts) { if (vistos.has(p.phone)) return false; vistos.add(p.phone); }
    const soloDigitos = linea.trim() && !/[a-záéíóúñ]/i.test(linea) && /\d{7,15}/.test(linea) && !parts.length;
    return !soloDigitos;
  });
  return lineas.join("\n");
}

/** Descarga un archivo que llegó como blob autenticado (copias, exportar TXT). */
export function descargarBlob(data, nombre, tipo) {
  const blob = data instanceof Blob ? data : new Blob([data], { type: tipo || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
